<template>
  <div
    v-if="pageDataLoaded && project"
  >
    <!-- DIALOGS -->
    <v-dialog
      v-model="confirmDeleteSiteLogs"
      max-width="290px"
    >
      <v-card>
        <v-card-title>
          Izbris dnevnih listov
        </v-card-title>
        <v-card-text>
          <ul>
            <li
              v-for="date in izbraniDnevnikiForDeletion"
              :key="date"
            >
              {{ formateDate(date) }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="confirmDeleteSiteLogs = false"
          >
            Prekliči
          </v-btn>
          <v-btn
            color="error"
            @click="deleteSelectedLogs"
          >
            Izbriši
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="projectNameEditDialogOpen"
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          Uredi ime projekta
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="projectNameEdited"
            data-cy="renamed-project-name"
            label="Naziv projekta"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="projectNameEditDialogOpen = false"
          >
            Zapri
          </v-btn>
          <v-btn
            data-cy="rename-project"
            color="primary"
            variant="outlined"
            @click="changeProjectName"
          >
            Shrani
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card
      data-cy="project-heading-row"
      class="mb-6"
    >
      <v-card-title class="d-flex align-center">
        <h1 class="text-headline-medium">
          {{ project.name }}
        </h1>
        <v-spacer />
        <v-btn
          v-if="canEditProject"
          data-cy="open-rename-project-dialog"
          icon="edit"
          variant="text"
          aria-label="Uredi ime projekta"
          @click="openProjectNameEditDialog"
        />
      </v-card-title>

      <v-tabs
        v-model="openedTab"
        data-cy="project-tabs"
        color="primary"
        show-arrows
      >
        <v-tab
          :value="0"
          @click="goTo('/projects/' + $route.params.projectId)"
        >
          Uvodni list
        </v-tab>
        <v-tab
          :value="1"
          data-cy="sitelogs-tab"
          @click="goTo('/projects/' + $route.params.projectId+ '/journals')"
        >
          Dnevni listi
        </v-tab>
        <v-tab
          :value="2"
          data-cy="photos-tab"
          @click="goTo('/projects/' + $route.params.projectId+ '/pictures')"
        >
          Slike
        </v-tab>
        <v-tab
          v-if="canManageProject"
          :value="3"
          @click="goTo('/projects/' + $route.params.projectId+ '/users')"
        >
          Uporabniški dostopi
        </v-tab>

        <v-tab
          v-if="canManageProject"
          :value="4"
          @click="goTo('/projects/' + $route.params.projectId+ '/hierarchy')"
        >
          Podprojekti
        </v-tab>
      </v-tabs>
    </v-card>

    <v-window v-model="openedTab">
      <v-window-item
        :value="0"
        class="pa-1"
      >
        <v-form
          data-cy="introductory-form"
        >
          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                1. Podatki o objektu, kot so navedeni v gradbenem dovoljenju
              </v-toolbar-title>
              <v-spacer />
              <v-btn
                icon="print"
                variant="text"
                aria-label="Natisni uvodni list"
                @click="printUvodniList"
              />
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="objekt__vrsta_objekta"
                :model-value="objekt.vrsta_objekta"
                :disabled="!canEditProject"
                label="Vrsta objekta glede na namen"
                @update:model-value="updateIntroductoryPage($event, 'vrsta_objekta', 1)"
              />
              <v-text-field
                name="objekt__kraj_gradnje"
                :model-value="objekt.kraj_gradnje"
                :disabled="!canEditProject"
                label="Kraj gradnje"
                @update:model-value="updateIntroductoryPage($event, 'kraj_gradnje', 1)"
              />
              <v-text-field
                name="objekt__kraj_za_vreme"
                :model-value="objekt.kraj_za_vreme"
                :disabled="!canEditProject"
                label="Alternativna lokacija za vreme (v kolikor kraj gradnje ne ustreza)"
                @update:model-value="updateIntroductoryPage($event, 'kraj_za_vreme', 1)"
              />
              <v-text-field
                name="objekt__vrsta_gradnje"
                :model-value="objekt.vrsta_gradnje"
                :disabled="!canEditProject"
                label="Vrsta gradnje"
                @update:model-value="updateIntroductoryPage($event, 'vrsta_gradnje', 1)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                2. Podatki o investitorju
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="investitor__naziv"
                :model-value="investitor.naziv"
                :disabled="!canEditProject"
                label="Naziv in sedež ali ime, priimek in naslov"
                @update:model-value="updateIntroductoryPage($event, 'naziv', 2)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                3. Podatki o izvajalcu
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="izvajalec__naziv"
                :model-value="izvajalec.naziv"
                :disabled="!canEditProject"
                label="Naziv in sedež"
                @update:model-value="updateIntroductoryPage($event, 'naziv', 3)"
              />
              <v-textarea
                name="izvajalec__odgovorni"
                :model-value="izvajalec.odgovorni"
                :disabled="!canEditProject"
                label="Odgovorni vodja s področja"
                auto-grow
                @update:model-value="updateIntroductoryPage($event, 'odgovorni', 3)"
              />
              <v-text-field
                name="izvajalec__vodja_gradbisca"
                :model-value="izvajalec.vodja_gradbisca"
                :disabled="!canEditProject"
                label="Odgovorni vodja gradbišča (če je imenovan)"
                @update:model-value="updateIntroductoryPage($event, 'vodja_gradbisca', 3)"
              />
              <v-textarea
                name="izvajalec__vodja_posameznih_del"
                :model-value="izvajalec.vodja_posameznih_del"
                :disabled="!canEditProject"
                label="Odgovorni vodja posameznih del s področja"
                auto-grow
                @update:model-value="updateIntroductoryPage($event, 'vodja_posameznih_del', 3)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                4. Podatki o projektu za pridobitev gradbenega dovoljenja
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="pridobitevGradbenegaDovoljenja__stevilka_in_datum"
                :model-value="pridobitevGradbenegaDovoljenja.stevilka_in_datum"
                :disabled="!canEditProject"
                label="Identifikacijska številka, datum izdelave, projektant"
                @update:model-value="updateIntroductoryPage($event, 'stevilka_in_datum', 4)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                5. Podatki o gradbenem dovoljenju
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="gradbenoDovoljenje__izdajatelj"
                :model-value="gradbenoDovoljenje.izdajatelj"
                :disabled="!canEditProject"
                label="Naziv organa, ki ga je izdal"
                @update:model-value="updateIntroductoryPage($event, 'izdajatelj', 5)"
              />
              <v-textarea
                name="gradbenoDovoljenje__stevilka_in_datum"
                :model-value="gradbenoDovoljenje.stevilka_in_datum"
                :disabled="!canEditProject"
                label="Številka in datum"
                auto-grow
                @update:model-value="updateIntroductoryPage($event, 'stevilka_in_datum', 5)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                6. Podatki o projektu za izvedbo
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="projektZaIzvedbo__stevilka_in_datum"
                :model-value="projektZaIzvedbo.stevilka_in_datum"
                :disabled="!canEditProject"
                label="Identifikacijska številka in datum izdelave"
                @update:model-value="updateIntroductoryPage($event, 'stevilka_in_datum', 6)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                7. Podatki o nadzorniku
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="nadzornik__naziv"
                :model-value="nadzornik.naziv"
                :disabled="!canEditProject"
                label="Naziv in sedež nadzornika"
                @update:model-value="updateIntroductoryPage($event, 'naziv', 7)"
              />
              <v-text-field
                name="nadzornik__odgovorna_oseba"
                :model-value="nadzornik.odgovorna_oseba"
                :disabled="!canEditProject"
                label="Odgovorni nadzornik"
                @update:model-value="updateIntroductoryPage($event, 'odgovorna_oseba', 7)"
              />
              <v-text-field
                name="nadzornik__odgovorna_oseba_posameznih_del"
                :model-value="nadzornik.odgovorna_oseba_posameznih_del"
                :disabled="!canEditProject"
                label="Odgovorni nadzornik posameznih del z navedbo področja (ime in priimek, strokovni ali poklicni naziv)"
                @update:model-value="updateIntroductoryPage($event, 'odgovorna_oseba_posameznih_del', 7)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                8. Pri rekonstrukciji objekta, varovanega na podlagi predpisov o varstvu kulturne dediščine ali pri gradnji na arheološkem območju
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="kulturnaDediscina__naziv"
                :model-value="kulturnaDediscina.naziv"
                :disabled="!canEditProject"
                label="Naziv in sedež pristojne službe za varstvo kulturne dediščine"
                @update:model-value="updateIntroductoryPage($event, 'naziv', 8)"
              />
              <v-text-field
                name="kulturnaDediscina__arheolog"
                :model-value="kulturnaDediscina.arheolog"
                :disabled="!canEditProject"
                label="Podatki o odgovornem konservatorju oz. arheologu (ime in priimek, strokovni ali poklicni naziv)"
                @update:model-value="updateIntroductoryPage($event, 'arheolog', 8)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                9. Predaja projekta za pridobitev gradbenega dovoljenja izvajalcu
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="predajaProjekta__datum"
                :model-value="predajaProjekta.datum"
                :disabled="!canEditProject"
                label="Datum"
                @update:model-value="updateIntroductoryPage($event, 'datum', 11)"
              />
              <v-text-field
                name="predajaProjekta__podpisi"
                :model-value="predajaProjekta.podpisi"
                :disabled="!canEditProject"
                label="Podpisi"
                @update:model-value="updateIntroductoryPage($event, 'podpisi', 11)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                10. Podatki o osebi, odgovorni za sestavo
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="osebaOdgovornaZaSestavo__odgovorni_gradbenega_dnevnika"
                :model-value="osebaOdgovornaZaSestavo.odgovorni_gradbenega_dnevnika"
                :disabled="!canEditProject"
                label="Gradbenega dnevnika"
                @update:model-value="updateIntroductoryPage($event, 'odgovorni_gradbenega_dnevnika', 9)"
              />
              <v-text-field
                name="osebaOdgovornaZaSestavo__odgovorni_obracunskih_mer"
                :model-value="osebaOdgovornaZaSestavo.odgovorni_obracunskih_mer"
                :disabled="!canEditProject"
                label="Knjige obračunskih izmer"
                @update:model-value="updateIntroductoryPage($event, 'odgovorni_obracunskih_mer', 9)"
              />
            </v-card-text>
          </v-card>

          <v-card class="mb-6">
            <v-toolbar>
              <v-toolbar-title>
                11. Dokumentacija o zagotavljanju varstva in zdravja pri delu na gradbišču
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <v-text-field
                name="varstvoInZdravjePriDelu__stevilka_varnostnega_nacrta"
                :model-value="varstvoInZdravjePriDelu.stevilka_varnostnega_nacrta"
                :disabled="!canEditProject"
                label="Številka varnostnega načrta"
                @update:model-value="updateIntroductoryPage($event, 'stevilka_varnostnega_nacrta', 10)"
              />
              <v-text-field
                name="varstvoInZdravjePriDelu__izvajalec"
                :model-value="varstvoInZdravjePriDelu.izvajalec"
                :disabled="!canEditProject"
                label="Izvajalec, pooblaščen za ureditev gradbišča v skladu z varnostnim načrtom"
                @update:model-value="updateIntroductoryPage($event, 'izvajalec', 10)"
              />
              <v-text-field
                name="varstvoInZdravjePriDelu__koordinator_za_varnost"
                :model-value="varstvoInZdravjePriDelu.koordinator_za_varnost"
                :disabled="!canEditProject"
                label="Koordinator za varnost (ime in priimek, strokovni ali poklicni naziv)"
                @update:model-value="updateIntroductoryPage($event, 'koordinator_za_varnost', 10)"
              />
            </v-card-text>
          </v-card>
        </v-form>
      </v-window-item>
      <v-window-item
        :value="1"
        class="pa-1"
      >
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Dnevni listi</v-toolbar-title>
            <v-spacer />

            <v-toolbar-items class="d-none d-md-flex">
              <v-switch
                v-if="false && canEditProject"
                class="ma-2"
                label="Avtomatsko kreiraj dnevne liste"
                :model-value="project.automatic_site_logs_enabled"
                @update:model-value="enableOrDisableAutomaticSiteLogs($event)"
              />
            </v-toolbar-items>

            <v-dialog
              ref="dialog"
              v-model="createDnevniListModal"
              persistent
              width="290px"
            >
              <template #activator="{ props }">
                <v-btn
                  v-if="canWriteSitelogs"
                  prepend-icon="add"
                  aria-label="Dodaj dnevni list"
                  data-cy="open-create-sitelog"
                  v-bind="props"
                  @click.stop="createDnevniListModal = true"
                >
                  Nov dnevni list
                </v-btn>
              </template>

              <v-date-picker
                :model-value="createDnevniListDate"
                first-day-of-week="1"
                locale="sl"
                @update:model-value="setCreateDnevniListDate"
              >
                <template #actions>
                  <v-spacer />
                  <v-btn
                    variant="text"
                    color="primary"
                    data-cy="cancel-create-sitelog"
                    @click="createDnevniListModal = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    variant="text"
                    color="primary"
                    data-cy="confirm-create-sitelog"
                    @click="createDnevniList(createDnevniListDate)"
                  >
                    OK
                  </v-btn>
                </template>
              </v-date-picker>
            </v-dialog>
          </v-toolbar>

          <v-list>
            <div class="d-flex align-center px-4 py-2">
              <div class="d-flex align-center">
                <v-checkbox
                  hide-details
                  @update:model-value="toggleSiteLogSelection($event)"
                />
              </div>

              <div class="d-flex align-center">
                <v-menu
                  location="bottom"
                  :offset="8"
                >
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                    >
                      filter_alt
                    </v-icon>

                    <span
                      v-show="sitelogFilters.vsi"
                      v-bind="props"
                    >Vsi <v-icon>arrow_drop_down</v-icon></span>
                    <span
                      v-show="sitelogFilters.nepodpisani"
                      v-bind="props"
                    >Nepodpisani <v-icon>arrow_drop_down</v-icon></span>
                    <span
                      v-show="sitelogFilters.podpisani"
                      v-bind="props"
                    >Podpisani <v-icon>arrow_drop_down</v-icon></span>
                  </template>
                  <v-list>
                    <v-list-item
                      @click="filterShowAllSiteLogs()"
                    >
                      <span v-show="sitelogFilters.vsi">✓ </span> Vsi
                    </v-list-item>
                    <v-list-item
                      @click="filterShowUnsignedSiteLogs()"
                    >
                      <span v-show="sitelogFilters.nepodpisani">✓ </span> Nepodpisani
                    </v-list-item>
                    <v-list-item
                      @click="filterShowSignedSiteLogs()"
                    >
                      <span v-show="sitelogFilters.podpisani">✓ </span> Podpisani
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <div
                v-if="izbraniDnevniki.length > 0"
                class="d-flex align-center"
              >
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  data-cy="delete-selected-sitelogs"
                  @click="confirmDeleteSiteLogs = true"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </div>

              <div
                v-if="izbraniDnevniki.length > 0"
                class="d-flex align-center"
              >
                <div v-if="canEditProject">
                  <SendJournalToSignature
                    :project-id="projectId"
                    :date="izbraniDnevniki"
                    :sequence="izbraniDnevnikiSequences"
                    :supervisors="projectSupervisors"
                    :nkks="projectNkks"
                    :zkks="projectZkks"
                    :is-signed="false"
                    monochrome
                  />
                </div>
              </div>

              <div
                v-if="izbraniDnevniki.length > 0"
                class="d-flex align-center"
              >
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  data-cy="print-selected-sitelogs"
                  @click="exportSelectedLogs"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </div>

              <div
                v-if="izbraniDnevniki.length > 0"
                class="d-flex align-center"
              >
                <v-switch
                  v-model="printWithImages"
                  label="Natisni s slikami"
                  hide-details
                />
              </div>

              <v-spacer />

              <div class="flex-grow-0">
                <v-list-item-subtitle class="text-lg-right">
                  Skupno št. listov: {{ dnevniki.length }}
                </v-list-item-subtitle>
              </div>
            </div>

            <v-divider />

            <v-list-item
              v-for="dnevnik in dnevniki"
              :key="dnevnik.id"
              data-cy="sitelog-row"
              link
              @click="goTo('/projects/' + dnevnik.project_id + '/journals/' + dnevnik.date + '/' + dnevnik.sequence)"
            >
              <template #prepend>
                <v-checkbox
                  v-if="canEditProject"
                  v-model="izbraniDnevniki"
                  :value="dnevnik.date"
                  data-cy="select-sitelog"
                  hide-details
                  @click.stop
                />
              </template>

              <template #title>
                <span>
                  <span v-show="dnevnik.locked_for_edit">🔏</span>
                  {{ dnevnik.display_name }}
                </span>
              </template>
              <template #subtitle>
                <span>
                  <span v-show="dnevnik.signed_by.nadzornik">✓ Podpisal: Nadzornik</span>
                  <span v-show="dnevnik.signed_by.sestavljalec"> ✓ Podpisal: Sestavljalec</span>
                  <span v-show="dnevnik.signed_by.odgovorni"> ✓ Podpisal: Odgovorni</span>
                </span>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-window-item>

      <v-window-item
        :value="2"
        class="pa-1"
      >
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Slike</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                variant="text"
                aria-label="Natisni slike"
                @click="printPhotos"
              >
                <v-icon>print</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>

          <v-card-text v-if="canAddPhotos">
            <v-form ref="imageUploadForm">
              <image-uploader
                ref="image"
                :max-width="2048"
                :quality="0.9"
                :preview="true"
                accept="image/*"
                do-not-resize="['gif', 'svg']"
                required
                :rules="imageRules"
                output-format="blob"
                @input="setImage"
              />
              <v-text-field
                :model-value="computedImageDate"
                label="Datum slike"
                prepend-icon="event"
                :rules="imageDateRules"
                readonly
                required
                @focus="newImageDatePickerOpen = true"
              />
              <v-date-picker
                v-show="newImageDatePickerOpen"
                :model-value="imageDate"
                hide-header
                locale="sl"
                first-day-of-week="1"
                @update:model-value="setImageDate"
              />
              <v-text-field
                v-model="imageDescription"
                label="Opis slike"
              />

              <v-btn
                color="primary"
                variant="outlined"
                data-cy="upload-project-photo"
                :loading="uploadInProgress"
                @click="uploadImage"
              >
                Naloži sliko
              </v-btn>
            </v-form>
          </v-card-text>

          <material-lightbox
            :model-value="routeImageUrl"
            :images="lightboxImages"
            @close="setLightboxImage('')"
            @select="setLightboxImage"
          />

          <v-container
            v-for="(images, date) in siteLogImagesGroupedByDate"
            :key="date"
            fluid
          >
            <h2 class="text-title-large">
              {{ humanReadableDate(date) }}
            </h2>
            <v-row>
              <v-col
                v-for="photo in images"
                :key="photo.image_id"
                cols="12"
                sm="6"
                md="4"
                class="d-flex"
              >
                <v-card
                  class="mx-auto"
                >
                  <v-card-text>
                    <v-img
                      :src="photo.thumbs[256][0]"
                      data-cy="uploaded-project-photo"
                      aspect-ratio="1"
                      min-width="104px"
                      class="bg-grey-lighten-2"
                      @click="showLightbox(photo)"
                    >
                      <template #placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular
                            indeterminate
                            color="grey-lighten-5"
                          />
                        </v-row>
                      </template>
                    </v-img>
                  </v-card-text>

                  <v-card-actions v-if="canEditProject">
                    <v-btn
                      icon
                      variant="text"
                      aria-label="Uredi sliko"
                      @click="openEditImageDialog(photo)"
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      aria-label="Prenesi sliko"
                      @click="downloadImage(photo)"
                    >
                      <v-icon>download</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <!-- DIALOGS -->
        <v-row
          justify="center"
        >
          <v-dialog
            v-model="imageEdit.dialogOpen"
            persistent
            max-width="600px"
          >
            <v-card>
              <v-card-title>
                Urejanje slike
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-text-field
                    :model-value="computedEditableImageDate"
                    label="Datum slike"
                    prepend-icon="event"
                    :rules="imageDateRules"
                    readonly
                    required
                    @focus="imageEdit.dateDialogOpen = true"
                  />
                  <v-date-picker
                    v-show="imageEdit.dateDialogOpen"
                    :model-value="imageEdit.imageData.date"
                    hide-header
                    locale="sl"
                    first-day-of-week="1"
                    @update:model-value="setEditableImageDate"
                  />
                  <v-text-field
                    v-model="imageEdit.imageData.description"
                    label="Opis slike"
                  />
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="error"
                  variant="text"
                  @click="deleteImage"
                >
                  Izbriši sliko
                </v-btn>
                <v-btn
                  color="primary"
                  variant="text"
                  @click="imageEdit.dialogOpen = false"
                >
                  Zapri
                </v-btn>
                <v-btn
                  color="primary"
                  variant="outlined"
                  @click="updateImage()"
                >
                  Shrani
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </v-window-item>

      <v-window-item
        :value="3"
        class="pa-1"
      >
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Uporabniki</v-toolbar-title>
          </v-toolbar>

          <v-card-text>
            <v-select
              :items="possibleProjectLeads"
              :model-value="projectLeads"
              item-value="id"
              item-title="display_name"
              :item-props="item => ({ disabled: item.disabled })"
              label="Uporabniki, ki lahko urejajo projekt"
              chips
              multiple
              @update:model-value="updateProjectLeads"
            />
          </v-card-text>

          <v-card-text>
            <v-select
              :items="possibleProjectSubcontractors"
              :model-value="projectSubcontractors"
              item-value="id"
              item-title="display_name"
              :item-props="item => ({ disabled: item.disabled })"
              label="Podizvajalci"
              chips
              multiple
              @update:model-value="updateProjectSubcontractors"
            />
          </v-card-text>

          <v-card-text>
            <v-select
              :items="possibleProjectReviewers"
              :model-value="projectReviewers"
              item-value="id"
              item-title="display_name"
              :item-props="item => ({ disabled: item.disabled })"
              label="Uporabniki, ki lahko samo pregledujejo projekt"
              chips
              multiple
              @update:model-value="updateProjectReviewers"
            />
          </v-card-text>

          <v-card-text>
            <v-select
              :items="possibleProjectSupervisors"
              :model-value="projectSupervisors"
              item-value="id"
              item-title="display_name"
              :item-props="item => ({ disabled: item.disabled })"
              label="Nadzorniki"
              chips
              multiple
              @update:model-value="updateProjectSupervisors"
            />
          </v-card-text>

          <v-card-text>
            <v-select
              :items="possibleProjectNkks"
              :model-value="projectNkks"
              item-value="id"
              item-title="display_name"
              :item-props="item => ({ disabled: item.disabled })"
              label="Notranja kontrola kakovosti"
              chips
              multiple
              @update:model-value="updateProjectNkks"
            />
          </v-card-text>

          <v-card-text>
            <v-select
              :items="possibleProjectZkks"
              :model-value="projectZkks"
              item-value="id"
              item-title="display_name"
              :item-props="item => ({ disabled: item.disabled })"
              label="Zunanja kontrola kakovosti"
              chips
              multiple
              @update:model-value="updateProjectZkks"
            />
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item
        :value="4"
        class="pa-1"
      >
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Podprojekti</v-toolbar-title>
          </v-toolbar>

          <v-card-text>
            <v-select
              :items="possibleProjectsForHierarchy"
              :model-value="projectHierarchy"
              data-cy="project-hierarchy"
              item-value="project_id"
              item-title="name"
              label="Podprojekti"
              chips
              multiple
              @update:model-value="updateProjectHierarchy"
            />
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>
  </div>
</template>

<script>
  import MainView from './MainView';
  import router from '../router';
  import moment from 'moment';
  import _ from 'lodash';
  import can_write_project from '../model/user/permissions/can_write_project';
  import {UserRole} from '@/model/user/UserRole';
  import SendJournalToSignature from '@/views/components/SendJournalToSignature';
  import can_create_new_project from '@/model/user/permissions/can_create_new_project';
  import can_add_photos from '../model/user/permissions/can_add_photos';
  import can_write_sitelog from '../model/user/permissions/can_write_sitelog';
  import MaterialLightbox from '@/views/components/MaterialLightbox';

  export default {
    components: { MaterialLightbox, SendJournalToSignature },
    extends: MainView,
    data () {
      return {
        pageDataLoaded: false,
        openedTab: this.$route.meta.tab || 0,
        createDnevniListModal: false,
        createDnevniListDate: moment().format('YYYY-MM-DD'),
        projectNameEditDialogOpen: false,
        projectNameEdited: '',
        journalHeaders: [
          { title: 'ID', key: 'id' },
          { title: 'Datum', key: 'date' }
        ],
        izbraniDnevniki: [],
        printWithImages: true,
        sitelogFilters: {
          vsi: true,
          nepodpisani: false,
          podpisani: false,
        },

        // Image upload,
        uploadInProgress: false,
        imageDate: moment().format('YYYY-MM-DD'),
        imageDateRules: [
          v => !!v || 'Datum je obvezen',
        ],
        image: null,
        imageName: null,
        imageRules: [
          v => !!v || 'Slika je obvezna',
        ],
        imageDescription: null,
        newImageDatePickerOpen: false,

        imageEdit: {
          dialogOpen    : false,
          dateDialogOpen: false,
          imageData     : {}
        },

        // Delete sitelog dialog
        confirmDeleteSiteLogs: false
      }
    },
    computed: {
      izbraniDnevnikiRecords() {
        return this.izbraniDnevniki
          .map(date => this.dnevniki.find(dnevnik => dnevnik.date === date))
          .filter(Boolean)
        ;
      },
      izbraniDnevnikiForDeletion() {
        return this.izbraniDnevnikiRecords
            .filter(dnevnik => !dnevnik.locked_for_edit)
            .map(dnevnik => dnevnik.date)
        ;
      },
      izbraniDnevnikiSequences() {
        return this.izbraniDnevnikiRecords.map(dnevnik => dnevnik.sequence);
      },
      canAddPhotos() {
        return can_add_photos(this.$store.state.currentUser, this.project);
      },
      canWriteSitelogs() {
        const currentUser = this.$store.state.currentUser;

        return can_write_sitelog(currentUser, this.project);
      },
      canEditProject() {
        const currentUser = this.$store.state.currentUser;

        return can_write_project(currentUser, this.project);
      },
      canManageProject() {
        return can_create_new_project(this.$store.state.currentUser);
      },
      projectId() {
        return this.$route.params.projectId;
      },
      project() {
        return this.$store.getters.projectSummary(this.$route.params.projectId);
      },
      companyId() {
        return this.$store.state.currentCompany.company_id;
      },
      objekt() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 1);
      },
      investitor() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 2);
      },
      izvajalec() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 3);
      },
      pridobitevGradbenegaDovoljenja() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 4);
      },
      gradbenoDovoljenje() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 5);
      },
      projektZaIzvedbo() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 6);
      },
      nadzornik() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 7);
      },
      kulturnaDediscina() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 8);
      },
      osebaOdgovornaZaSestavo() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 9);
      },
      varstvoInZdravjePriDelu() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 10);
      },
      predajaProjekta() {
        return this.$store.getters.uvodniListSection(this.$route.params.projectId, 11);
      },
      dnevniki() {
        let startSequence = 1;

        return this.$store.getters
          .seznamDnevnihListov(this.$route.params.projectId)
          .map(d => {
            const nextSequence = _.padStart(startSequence++, 3, '0');

            return {
              ...d,
              sequence    : nextSequence,
              display_name: nextSequence + ' ' + moment(d.date).format('dddd, DD.MM.YYYY')
            };
          })
          .filter(d => {
            if (this.sitelogFilters.nepodpisani) {
              return !(Object.values(d.signed_by).every(isSigned => isSigned));
            }

            if (this.sitelogFilters.podpisani) {
              return (Object.values(d.signed_by).every(isSigned => isSigned));
            }

            return true;
          })
          .reverse()
        ;
      },
      siteLogImagesGroupedByDate() {
        return _.groupBy(
          _.orderBy(this.$store.getters.siteLogImages(this.$route.params.projectId), ['date'], ['desc']),
          'date'
        );
      },
      computedImageDate() {
        return moment(this.imageDate).format('DD.MM.YYYY');
      },
      computedEditableImageDate() {
        return moment(this.imageEdit.imageData.date).format('DD.MM.YYYY');
      },

      // Lightbox
      routeImageUrl() {
        return typeof this.$route.query.imageUrl === 'string' ? this.$route.query.imageUrl : '';
      },
      lightboxImages() {
        return _.orderBy(this.$store.getters.siteLogImages(this.$route.params.projectId), ['date'], ['desc'])
          .map(i => {
            const humanReadableDate = moment(i.date).format('dddd, DD.MM.YYYY');

            return {
              name: i.thumbs[1024][0],
              alt : `${humanReadableDate}: ${i.description || 'Ni opisa'}`,
            };
          })
        ;
      },

      // User management
      possibleProjectLeads() {
        return this
          .$store
          .getters
          .possibleProjectLeads(this.projectId)
          .map(u => {
            return {
              ...u,
              display_name: `${u.first_name} ${u.last_name}`
            };
          })
        ;
      },
      projectLeads() {
        return this
          .$store
          .getters
          .projectLeads(this.projectId)
          .map(u => {
            return {
              ...u,
              display_name: `${u.first_name} ${u.last_name} <${u.role}>`,
              disabled    : u.role === 'admin'
            };
          })
        ;
      },
      possibleProjectSubcontractors() {
        return this
            .$store
            .getters
            .possibleProjectSubcontractors(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name}`
              };
            })
            ;
      },
      projectSubcontractors() {
        return this
            .$store
            .getters
            .projectSubcontractors(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name} <${u.role}>`,
                disabled    : u.role === 'admin'
              };
            })
            ;
      },
      possibleProjectReviewers() {
        return this
          .$store
          .getters
          .possibleProjectReviewers(this.projectId)
          .map(u => {
            return {
              ...u,
              display_name: `${u.first_name} ${u.last_name}`
            };
          })
        ;
      },
      projectReviewers() {
        return this
          .$store
          .getters
          .projectReviewers(this.projectId)
          .map(u => {
            return {
              ...u,
              display_name: `${u.first_name} ${u.last_name} <${u.role}>`,
              disabled    : u.role === 'admin'
            };
          })
        ;
      },
      possibleProjectSupervisors() {
        return this
            .$store
            .getters
            .possibleProjectSupervisors(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name}`
              };
            })
          ;
      },
      projectSupervisors() {
        return this
            .$store
            .getters
            .projectSupervisors(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name} <${u.role}>`,
                disabled    : u.role === 'admin'
              };
            })
          ;
      },
      possibleProjectNkks() {
        return this
            .$store
            .getters
            .possibleProjectNkks(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name}`
              };
            })
        ;
      },
      projectNkks() {
        return this
            .$store
            .getters
            .projectNkks(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name} <${u.role}>`,
                disabled    : u.role === 'admin'
              };
            })
            ;
      },
      possibleProjectZkks() {
        return this
            .$store
            .getters
            .possibleProjectZkks(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name}`
              };
            })
            ;
      },
      projectZkks() {
        return this
            .$store
            .getters
            .projectZkks(this.projectId)
            .map(u => {
              return {
                ...u,
                display_name: `${u.first_name} ${u.last_name} <${u.role}>`,
                disabled    : u.role === 'admin'
              };
            })
            ;
      },
      possibleProjectsForHierarchy() {
        return this
          .$store
          .getters
          .listOfProjects()
          .filter(p => p.project_id !== this.projectId)
      },
      projectHierarchy() {
        return (this.project.subprojects || [])
          .map(projectId => {
            return this
                .$store
                .getters
                .listOfProjects()
                .find(p => p.project_id === projectId);
          })
        ;
      }
    },
    watch: {
      '$route': function (route, previousRoute) {
        if (route.meta.tab === 4 && previousRoute.meta.tab !== 4) {
          this.$store.dispatch('fetchProjects');
        }
      }
    },
    async created() {
      await Promise.all([
        this.$store.dispatch(this.$route.meta.tab === 4 ? 'fetchProjects' : 'fetchProject', this.$route.params.projectId),
        this.$store.dispatch('fetchSiteLogs', this.$route.params.projectId),
        this.$store.dispatch('fetchSiteLogImages', this.$route.params.projectId),
        this.$store.dispatch('fetchUsers'),
      ]);

      this.pageDataLoaded = true;
    },
    methods: {
      humanReadableDate(date) {
        return moment(date).format('dddd, DD.MM.YYYY');
      },
      goTo(href) {
        if (this.$route.path !== href) {
          router.push(href);
        }
      },
      devNull() {
        /* Needed to add click handler to some tiles in order to highlight on hover */
        return;
      },
      formateDate(date) {
        return moment(date).format('dddd, DD.MM.YYYY');
      },
      formatDatePickerDate(date) {
        return moment(date).format('YYYY-MM-DD');
      },
      setCreateDnevniListDate(date) {
        this.createDnevniListDate = this.formatDatePickerDate(date);
      },
      setImageDate(date) {
        this.imageDate = this.formatDatePickerDate(date);
        this.newImageDatePickerOpen = false;
      },
      setEditableImageDate(date) {
        this.imageEdit.imageData.date = this.formatDatePickerDate(date);
        this.imageEdit.dateDialogOpen = false;
      },
      openProjectNameEditDialog() {
        this.projectNameEdited         = this.project.name;
        this.projectNameEditDialogOpen = true;
      },
      async changeProjectName() {
        const projectId = this.$route.params.projectId;
        const name      = this.projectNameEdited;

        await this.$store.dispatch('changeProjectName', { projectId, name });

        this.projectNameEditDialogOpen = false;
      },
      async createDnevniList(forDate) {
        this.createDnevniListModal = false;
        await this.$store.dispatch('createNewSiteLog', {
          projectId: this.$route.params.projectId,
          date: this.formatDatePickerDate(forDate)
        });

        this.izbraniDnevniki = [];
      },
      async updateIntroductoryPage(value, fieldPath, sectionNumber) {
        const projectId = this.$route.params.projectId;
        this.$store.dispatch('updateIntroductoryPageSection', { value, projectId, fieldPath, sectionNumber });
      },
      toggleSiteLogSelection(isChecked) {
        if (isChecked) {
          this.izbraniDnevniki = this.dnevniki.map(d => d.date);
        } else {
          this.izbraniDnevniki = [];
        }
      },
      filterShowAllSiteLogs() {
        this.izbraniDnevniki = [];

        this.sitelogFilters = {
          vsi: true,
          nepodpisani: false,
          podpisani: false,
        };
      },
      filterShowUnsignedSiteLogs() {
        this.izbraniDnevniki = [];

        this.sitelogFilters = {
          vsi: false,
          nepodpisani: true,
          podpisani: false,
        };
      },
      filterShowSignedSiteLogs() {
        this.izbraniDnevniki = [];

        this.sitelogFilters = {
          vsi: false,
          nepodpisani: false,
          podpisani: true,
        };
      },
      async deleteSelectedLogs() {
        await Promise.all(this
            .izbraniDnevniki
            .map(date => {
              return this.$store.dispatch('deleteSiteLog', {
                projectId: this.$route.params.projectId,
                date     : date
              });
        }));

        this.izbraniDnevniki       = [];
        this.confirmDeleteSiteLogs = false;
      },
      async exportSelectedLogs() {
        const exportParams =
          _.orderBy(
            this
              .dnevniki
              .filter(d => this.izbraniDnevniki.includes(d.date))
            ,['date'], ['asc'])
          .map(d => `${d.date},${d.sequence}`)
          .join(';')
        ;

        const url = `${this.$config.API_PDF_EXPORT_BASE}/dnevni-list/${this.$route.params.projectId}/${exportParams}?withPhotos=${this.printWithImages}`;

        window.open(url, '_blank');
      },
      async uploadImage() {
        if ((await this.$refs.imageUploadForm.validate()).valid) {
          this.uploadInProgress = true;

          const projectId   = this.$route.params.projectId;
          const image       = this.image;
          const imageName   = this.imageName;
          const imageDate   = this.imageDate;
          const description = this.imageDescription;

          await this.$store.dispatch('uploadSiteLogImage', {
            projectId,
            image,
            imageName,
            imageDate,
            description
          });

          this.uploadInProgress = false;
          this.imageDescription = '';
        }
      },
      showLightbox(image) {
        const imageUrl = image.thumbs[1024][0];
        this.setLightboxImage(imageUrl);
      },
      setLightboxImage(imageUrl) {
        const query = { ...this.$route.query };

        if (imageUrl) {
          query.imageUrl = imageUrl;
        } else {
          delete query.imageUrl;
        }

        router.push({ path: this.$route.path, query });
      },
      openEditImageDialog(image) {
        this.imageEdit.dialogOpen = true;
        this.imageEdit.imageData  = Object.assign({}, image);
      },
      downloadImage(image) {
        window.open(image.download_url, '_blank');
      },
      setImage(image, imageName) {
        this.image     = image;
        this.imageName = imageName;
      },
      async updateImage() {
        this.imageEdit.dialogOpen = false;

        const projectId   = this.$route.params.projectId;
        const imageId     = this.imageEdit.imageData.image_id;
        const date        = this.imageEdit.imageData.date;
        const description = this.imageEdit.imageData.description;

        await this.$store.dispatch('updateSiteLogImage', {
          projectId,
          imageId,
          date,
          description
        });
      },
      async deleteImage() {
        this.imageEdit.dialogOpen = false;

        const projectId = this.$route.params.projectId;
        const imageId   = this.imageEdit.imageData.image_id;

        await this.$store.dispatch('deleteSiteLogImage', {
          projectId,
          imageId
        });
      },
      async printUvodniList() {
        const url = `${this.$config.API_PDF_EXPORT_BASE}/uvodni-list/${this.$route.params.projectId}`;

        window.open(url, '_blank');
      },

      async printPhotos() {
        const url = `${this.$config.API_PDF_EXPORT_BASE}/photos/${this.companyId}/${this.$route.params.projectId}`;

        window.open(url, '_blank');
      },

      async updateProjectLeads(selectedItems) {
        await this.$store.dispatch('setProjectLeads', {
          projectId: this.projectId,
          userIds  : selectedItems,
        })
      },
      async updateProjectSubcontractors(selectedItems) {
        await this.$store.dispatch('setProjectSubcontractors', {
          projectId: this.projectId,
          userIds  : selectedItems,
        })
      },
      async updateProjectReviewers(selectedItems) {
        await this.$store.dispatch('setProjectReviewers', {
          projectId: this.projectId,
          userIds  : selectedItems,
        })
      },
      async updateProjectSupervisors(selectedItems) {
        await this.$store.dispatch('setProjectSupervisors', {
          projectId: this.projectId,
          userIds  : selectedItems,
        });
      },
      async updateProjectNkks(selectedItems) {
        await this.$store.dispatch('setProjectNkks', {
          projectId: this.projectId,
          userIds  : selectedItems,
        });
      },
      async updateProjectZkks(selectedItems) {
        await this.$store.dispatch('setProjectZkks', {
          projectId: this.projectId,
          userIds  : selectedItems,
        });
      },
      async updateProjectHierarchy(selectedItems) {
        await this.$store.dispatch('setProjectHierarchy', {
          projectId: this.projectId,
          childProjectIds  : selectedItems,
        });
      },
      async enableOrDisableAutomaticSiteLogs(enabled) {
        await this.$store.dispatch('setAutomaticSiteLogs', {
          projectId: this.projectId,
          enabled: enabled
        })
      }
    }
  }
</script>
